import { useQuery } from '@tanstack/react-query';
import { Card, DatePicker, List, Pagination, Select, Tag, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { auditApi, usersApi } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import type { AuditAction, AuditEntity, AuditFeedEntry } from '../../api/types';
import { ModalSelect } from '../../components/ModalSelect';
import { ErrorBlock, LoadingBlock } from '../../components/PageStates';

const ENTITIES: AuditEntity[] = [
  'OPERATION',
  'SHIPMENT',
  'SHIPMENT_ITEM',
  'SHIPMENT_MOVEMENT',
  'STORE',
  'SKLAD',
  'EXPENSE_CATEGORY',
  'MOYSKLAD_SETTINGS',
  'USER',
];

const ACTIONS: AuditAction[] = [
  'CREATE',
  'UPDATE',
  'DELETE',
  'RESTORE',
  'POST',
  'UNPOST',
  'TRANSFER',
  'RETURN',
  'UNDO_MOVEMENT',
  'PRICE_CHANGE',
  'PASSWORD_CHANGE',
  'PASSWORD_RESET',
  'ACTIVATE',
  'DEACTIVATE',
];

const PAGE_SIZE = 30;

/** «Кто что сделал» — the superadmin activity feed (SPEC §1). A dedicated,
 * filterable page (the owner opted out of per-row actor badges). */
export function HistoryTab(): JSX.Element {
  const { t } = useTranslation();
  const [entityType, setEntityType] = useState<AuditEntity | undefined>();
  const [action, setAction] = useState<AuditAction | undefined>();
  const [userId, setUserId] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [page, setPage] = useState(1);

  const usersQuery = useQuery({ queryKey: queryKeys.users, queryFn: usersApi.list });

  const params = {
    ...(entityType ? { entityType } : {}),
    ...(action ? { action } : {}),
    ...(userId ? { userId } : {}),
    ...(range ? { from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') } : {}),
    page,
    pageSize: PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: queryKeys.audit(params),
    queryFn: () => auditApi.list(params),
  });

  // A filter change resets to page 1.
  const onFilter = (fn: () => void): void => {
    setPage(1);
    fn();
  };

  const entityLabel = (e: AuditEntity): string => t(`settings.history.entity.${e}`);
  const actionLabel = (a: AuditAction): string => t(`settings.history.action.${a}`);

  const describe = (row: AuditFeedEntry): string =>
    row.summary ?? `${entityLabel(row.entityType)}: ${actionLabel(row.action)}`;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <ModalSelect
          allowClear
          size="middle"
          style={{ minWidth: 160, flex: '1 1 160px' }}
          placeholder={t('settings.history.filterUser')}
          title={t('settings.history.filterUser')}
          value={userId}
          onChange={(v) => onFilter(() => setUserId(v))}
          options={(usersQuery.data ?? []).map((u) => ({ value: u.id, label: u.name }))}
        />
        <Select
          allowClear
          style={{ minWidth: 140, flex: '1 1 140px' }}
          placeholder={t('settings.history.filterEntity')}
          value={entityType}
          onChange={(v) => onFilter(() => setEntityType(v))}
          options={ENTITIES.map((e) => ({ value: e, label: entityLabel(e) }))}
        />
        <Select
          allowClear
          style={{ minWidth: 140, flex: '1 1 140px' }}
          placeholder={t('settings.history.filterAction')}
          value={action}
          onChange={(v) => onFilter(() => setAction(v))}
          options={ACTIONS.map((a) => ({ value: a, label: actionLabel(a) }))}
        />
        <DatePicker.RangePicker
          style={{ flex: '1 1 220px' }}
          value={range}
          onChange={(v) => onFilter(() => setRange(v as [Dayjs, Dayjs] | null))}
        />
      </div>

      {query.isLoading ? (
        <LoadingBlock />
      ) : query.isError || !query.data ? (
        <ErrorBlock onRetry={() => void query.refetch()} />
      ) : query.data.items.length === 0 ? (
        <Typography.Text type="secondary">{t('settings.history.empty')}</Typography.Text>
      ) : (
        <>
          <Card size="small" styles={{ body: { paddingTop: 0, paddingBottom: 0 } }}>
            <List
              dataSource={query.data.items}
              renderItem={(row) => (
                <List.Item style={{ paddingInline: 4 }}>
                  <List.Item.Meta
                    title={describe(row)}
                    description={
                      <Typography.Text type="secondary">
                        <Tag>{row.userName ?? t('settings.history.system')}</Tag>
                        {dayjs(row.createdAt).format('DD.MM.YYYY HH:mm')}
                      </Typography.Text>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
          <Pagination
            align="center"
            current={page}
            pageSize={PAGE_SIZE}
            total={query.data.total}
            showSizeChanger={false}
            onChange={setPage}
          />
        </>
      )}
    </div>
  );
}
